import { io, type Socket } from "socket.io-client";
import { DASH_SOCKET_EVENTS, DASH_SUBSCRIBE_TOPICS } from "./events";
import { getActiveSite } from "@/api/client";

export type DashSocket = Socket;

let socket: DashSocket | null = null;
let socketSite: string | undefined;

export function getDashSocket(): DashSocket | null {
  return socket;
}

export function connectDashSocket(site?: string): DashSocket {
  const activeSite = site || getActiveSite();
  if (socket?.connected && socketSite === activeSite) return socket;

  if (socket) {
    try { socket.disconnect(); } catch { /* ignore */ }
    socket = null;
    socketSite = undefined;
  }

  socketSite = activeSite;
  socket = io("/dash", {
    path: "/socket.io",
    transports: ["websocket", "polling"],
    query: activeSite ? { site: activeSite } : undefined
  });

  socket.on("connect", () => {
    socket?.emit(DASH_SOCKET_EVENTS.subscribe, { topics: [...DASH_SUBSCRIBE_TOPICS] });
    if (activeSite) socket?.emit(DASH_SOCKET_EVENTS.selectSite, { site: activeSite });
  });

  return socket;
}

export function disconnectDashSocket(): void {
  socket?.disconnect();
  socket = null;
  socketSite = undefined;
}

/** RPC with ack — mirrors legacy `dashRpc`. */
export function dashRpc<TReq = Record<string, unknown>, TRes = unknown>(
  event: string,
  body?: TReq,
  timeoutMs = 30_000
): Promise<TRes> {
  return new Promise((resolve) => {
    const s = getDashSocket();
    if (!s?.connected) {
      resolve({ ok: false, error: "socket_not_connected" } as TRes);
      return;
    }
    const site = getActiveSite();
    const payload = {
      ...(body && typeof body === "object" ? (body as Record<string, unknown>) : {}),
      ...(site ? { site } : {})
    };
    let done = false;
    const timer = window.setTimeout(() => {
      if (done) return;
      done = true;
      resolve({ ok: false, error: "timeout" } as TRes);
    }, Math.max(250, Math.floor(Number(timeoutMs) || 30_000)));
    s.emit(event, payload, (resp: TRes) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      resolve(resp ?? ({ ok: false, error: "empty_response" } as TRes));
    });
  });
}
